import type {
  ItineraryResourceEntriesParseResult,
  ItineraryResourceEntryParseResult,
  ItineraryResourceParseContext,
  ItineraryResourcePreview,
  ItineraryResourceType,
} from "./types";
import {
  pointQuickEntryResourceEntryType,
  pointQuickEntryVisibleTypeLabel,
  transportQuickEntryPrefix,
} from "./config";

const CHINESE_NUMBER_CHARS = "零〇一二两三四五六七八九十百千万点";

/** 将业务输入中用于数量/价格的中文数字转换为阿拉伯数字。 */
function parseChineseNumber(value: string): number | null {
  if (!value || !new RegExp(`^[${CHINESE_NUMBER_CHARS}]+$`).test(value)) return null;
  if (value.includes("点")) {
    const [integer, fraction] = value.split("点");
    const integerValue = integer ? parseChineseNumber(integer) : 0;
    if (integerValue === null || !fraction || /[十百千万]/.test(fraction)) return null;
    const digits = [...fraction].map((digit) => ({ 零: 0, 〇: 0, 一: 1, 二: 2, 两: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9 }[digit]));
    if (digits.some((digit) => digit === undefined)) return null;
    return Number(`${integerValue}.${digits.join("")}`);
  }
  const digits: Record<string, number> = { 零: 0, 〇: 0, 一: 1, 二: 2, 两: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9 };
  if (![...value].some((digit) => "十百千万".includes(digit))) {
    const direct = [...value].map((digit) => digits[digit]);
    return direct.some((digit) => digit === undefined) ? null : Number(direct.join(""));
  }
  let total = 0;
  let section = 0;
  let current = 0;
  for (const digit of value) {
    if (digit in digits) {
      current = digits[digit];
      continue;
    }
    const unit = { 十: 10, 百: 100, 千: 1000, 万: 10000 }[digit];
    if (!unit) return null;
    if (unit === 10000) {
      section += current;
      total += section * unit;
      section = 0;
    } else {
      section += (current || 1) * unit;
    }
    current = 0;
  }
  return total + section + current;
}

/** 归一全角数字/乘号，并转换数量、价格和包车日号位置的中文数字。 */
export function normalizeItineraryResourceNumericText(text: string): string {
  const token = new RegExp(`[${CHINESE_NUMBER_CHARS}]+(?=\\s*(?:人|晚|天|餐|辆|座|元|往返接送|接送|单送|单接|包车|[xX×*]|[-,，;；]|$))`, "g");
  return text
    .normalize("NFKC")
    .replace(/(?<!\d)\d{1,3}(?:[,，]\d{3})+(?:\.\d+)?(?!\d)/g, (value) => value.replace(/[,，]/g, ""))
    .replace(token, (value) => String(parseChineseNumber(value) ?? value));
}

/** 识别显式负金额或负数量；数字紧邻连字符的日段（如 1-14）不视为负值。 */
function negativeNumericField(text: string): "unitPrice" | "quantity" | null {
  const matched = text.match(
    /(?:^|[^\d])(?:[-−‐‑‒–—―﹘﹣－]|负)\s*\d+(?:\.\d+)?\s*(?:(元|人|晚|天|餐|辆|座|个?\s*项目|[xX×*])|$)/u,
  );
  if (!matched) return null;
  const suffix = matched[1]?.replace(/\s/g, "");
  return !suffix || suffix === "元" ? "unitPrice" : "quantity";
}

/** 构造同时兼容字符串与结构化字段的失败结果。 */
function failure(
  code: string,
  field: string,
  message: string,
  value?: string,
  candidates?: string[],
): ItineraryResourceEntryParseResult {
  return {
    ok: false,
    error: message,
    reason: { code, field, ...(value ? { value } : {}), message, ...(candidates ? { candidates } : {}) },
  };
}

/** 按正则提取首个数字分组。 */
function matchNumber(text: string, pattern: RegExp): number | null {
  const matched = text.match(pattern);
  return matched ? Number(matched[1]) : null;
}

type ExtractedPrice = { value: number; text: string };

const RAIL_ROUTE_CODE_TOKEN_PATTERN = /(^|\s)[DGCKZT]\d{1,4}(?=\s|$)/giu;
const RAIL_ROUTE_CODE_SUFFIX_PATTERN = /\s+([DGCKZT]\d{1,4})$/iu;
const RAIL_SEAT_CLASS_SUFFIX_PATTERN = /\s+(商务座|特等座|一等座|二等座|动卧|软卧|硬卧|软座|硬座|无座)$/u;

/** 排除独立火车车次 token，避免其中数字成为裸价格候选。 */
function withoutRailRouteCode(text: string): string {
  return text.replace(RAIL_ROUTE_CODE_TOKEN_PATTERN, "$1");
}

/** 提取显式元价或排除数量单位后的最后一个裸价格，同时保留精确十进制文本。 */
function extractPrice(text: string): ExtractedPrice | null {
  const yuan = text.match(/(\d+(?:\.\d+)?)\s*元/);
  if (yuan) return { value: Number(yuan[1]), text: yuan[1] };
  const candidates = [...text.matchAll(/\d+(?:\.\d+)?/g)].filter((matched) => {
    const suffix = text.slice((matched.index ?? 0) + matched[0].length);
    return !/^\s*(?:人|晚|天|餐|辆|座)/.test(suffix);
  });
  const candidate = candidates.at(-1)?.[0];
  return candidate ? { value: Number(candidate), text: candidate } : null;
}

/** 无场景上下文时兼容旧调用；有上下文时绝不根据文本覆盖当前场景。 */
function detectResourceType(text: string): ItineraryResourceType {
  if (/住宿/.test(text)) return "住宿";
  if (/餐|正餐/.test(text)) return "餐";
  if (/飞机|机票|火车|高铁|动车|大交通|二等座|一等座|商务座|公务舱|经济舱|头等舱|硬卧|软卧|硬座/.test(text)) return "大交通";
  if (/大巴|包车|\d+\s*座/.test(text)) return "包车";
  if (/门票|索道|缆车|观光车|景交车|景区车|交通车|考察/.test(text)) return "考察点";
  if (/导游|司机|餐补|摊销/.test(text)) return "导游";
  return "杂费";
}

/** 按资源大类派生预览子类型。 */
function detectSubtype(text: string, resourceType: ItineraryResourceType): string {
  switch (resourceType) {
    case "住宿": return "住宿预算";
    case "餐": return "餐预算";
    case "大交通": return /火车|高铁|动车/.test(text) ? "火车" : "飞机";
    case "包车": return /单接/.test(text) ? "单接" : /单送/.test(text) ? "单送" : /接送/.test(text) ? "接送" : "包车";
    case "考察点": return detectPointEntryType(text);
    case "导游": return /导服费|导游服务|导服/.test(text)
      ? "导服费"
      : /路补/.test(text)
        ? "路补"
        : /大交通/.test(text)
          ? "大交通"
          : "餐补";
    default: {
      const nums = text.match(/\d+(?:\.\d+)?/g) ?? [];
      return /快递|税|整|固定/.test(text) || nums.length <= 1 ? "固定杂费" : "人头杂费";
    }
  }
}

/** 提取由空白或记录分隔符界定的角色，避免“大学生”“带队老师”误触发。 */
function separatedRoleMatches(text: string): string[] {
  return [...text.matchAll(/(?:^|[\s,，;；])(学生|老师|导游|司机)(?=$|[\s,，;；xX×*\d])/g)]
    .map((match) => match[1]);
}

/** 兼容原始图片中的“班线学生 20”，其他项目名不开放紧贴角色猜测。 */
function inlineBusRole(text: string, resourceType: ItineraryResourceType): string | null {
  if (resourceType !== "包车") return null;
  return text.replace(/^@/, "").match(
    /^班线(学生|老师|导游|司机)(?=\s*(?:[xX×*]\s*)?\d)/,
  )?.[1] ?? null;
}

/** 兼容“老师餐费”“导游餐补”等已建立的费用名前缀，不匹配“大学生”“带队老师”。 */
function leadingCostRole(text: string): string | null {
  return text.replace(/^@/, "").match(
    /^(学生|老师|导游|司机)(?=餐费|餐补|路补|大交通|门票|住宿费|导服费|服务费)/,
  )?.[1] ?? null;
}

const DECIMAL_VALUE_EXPRESSION = String.raw`\d+(?:\.\d+)?`;
const ROLE_VALUE_EXPRESSION = String.raw`\s*(?:[xX×*]\s*)?(?:(?:${DECIMAL_VALUE_EXPRESSION}\s*人\s*(?:[xX×*]\s*)?)|(?:${DECIMAL_VALUE_EXPRESSION}\s*[xX×*]\s*))?(?:${DECIMAL_VALUE_EXPRESSION}\s*[xX×*]\s*)?${DECIMAL_VALUE_EXPRESSION}\s*(?:元)?`;

type MultiplicativePriceFormula = {
  prefix: string;
  quantityFactor: number;
  quantityFactorText: string;
  billingFactor: number;
  unitPrice: number;
  unitPriceText: string;
};

/** 解析末尾“数量 x 单价”或“数量 x 次数 x 单价”，核价模型把前两因子折入数量。 */
function parseMultiplicativePriceFormula(text: string): MultiplicativePriceFormula | null {
  const number = String.raw`(\d+(?:\.\d+)?)`;
  const triple = text.match(new RegExp(
    String.raw`^(.*?)\s*${number}\s*(?:人\s*)?[xX×*]\s*${number}\s*(?:餐|次)?\s*[xX×*]\s*${number}\s*(?:元)?$`,
    "u",
  ));
  const pair = triple
    ? null
    : text.match(new RegExp(
      String.raw`^(.*?)\s*${number}\s*(?:人\s*)?[xX×*]\s*${number}\s*(?:元)?$`,
      "u",
    ));
  const matched = triple ?? pair;
  if (!matched) return null;
  const quantityFactorText = matched[2];
  const billingFactorText = triple ? matched[3] : "1";
  const unitPriceText = triple ? matched[4] : matched[3];
  return {
    prefix: matched[1].trim(),
    quantityFactor: Number(quantityFactorText),
    quantityFactorText,
    billingFactor: Number(billingFactorText),
    unitPrice: Number(unitPriceText),
    unitPriceText,
  };
}

/** 提取紧贴路线或考察点类型、且后接数值表达式的角色片段。 */
function attachedRoleValueMatches(text: string, resourceType: ItineraryResourceType): string[] {
  if (resourceType !== "大交通" && resourceType !== "考察点") return [];
  return [...text.matchAll(new RegExp(
    String.raw`(?<=[^\s,，;；])(学生|老师|导游|司机)(?=${ROLE_VALUE_EXPRESSION})`,
    "g",
  ))].map((match) => match[1]);
}

/** 兼容交通路线末尾和考察点已知类型后紧邻数值的角色，避免有效录入静默退化为单项。 */
function attachedRoleNearValue(text: string, resourceType: ItineraryResourceType): string | null {
  const valueSuffix = String.raw`(?=${ROLE_VALUE_EXPRESSION}$)`;
  if (resourceType === "大交通") {
    return text.match(new RegExp(
      String.raw`^(?:飞机|火车|高铁)\s+.*(学生|老师|导游|司机)${valueSuffix}`,
    ))?.[1] ?? null;
  }
  if (resourceType === "考察点") {
    return text.match(new RegExp(
      String.raw`(?:门票|耳麦|讲解|索道|缆车|观光车|景交车|景区车|交通车|游船|小火车|体验|课程|表演)(学生|老师|导游|司机)${valueSuffix}`,
    ))?.[1] ?? null;
  }
  return null;
}

/** 识别预算行旅客角色。 */
function detectRole(text: string, resourceType: ItineraryResourceType): string | null {
  return separatedRoleMatches(text).at(-1)
    ?? inlineBusRole(text, resourceType)
    ?? leadingCostRole(text)
    ?? attachedRoleNearValue(text, resourceType);
}

/** 只识别靠近数值口径的角色词，避免项目名称误触发人口联动。 */
function detectRoleNearValue(text: string, resourceType: ItineraryResourceType): string | null {
  const matches = [...text.matchAll(
    /(?:^|[\s,，;；])(学生|老师|导游|司机)(?=(?:\s+(?:[xX×*]\s*)?(?:\d|人|晚|天|餐|元|[零〇一二两三四五六七八九十百千万点])|(?:[xX×*]\s*)?(?:\d|[零〇一二两三四五六七八九十百千万点])))/g,
  )].map((match) => match[1]);
  return matches.at(-1)
    ?? inlineBusRole(text, resourceType)
    ?? leadingCostRole(text)
    ?? attachedRoleNearValue(text, resourceType);
}

/** 归一考察点已知中文子类型。 */
function detectPointEntryType(text: string): string {
  if (/耳麦|讲解/.test(text)) return "讲解服务";
  if (/小景点/.test(text)) return "小景点";
  if (/索道|缆车/.test(text)) return "索道";
  if (/观光车|景交车|景区车|交通车/.test(text)) return "景交车";
  if (/游船/.test(text)) return "游船";
  if (/小火车/.test(text)) return "小火车";
  if (/体验|课程/.test(text)) return "体验/课程";
  if (/表演/.test(text)) return "表演";
  if (/首道门票/.test(text)) return "首道门票";
  return "门票";
}

/** 提取角色词之前的完整考察点可见类型标签，避免前缀包含造成误匹配。 */
function pointVisibleLabelBeforeRole(text: string, role: string | null): string | null {
  if (!role) return null;
  const normalized = text.trim().replace(/\s+/g, " ");
  const roleIndex = normalized.lastIndexOf(role);
  if (roleIndex <= 0) return null;
  const suffix = normalized.slice(roleIndex + role.length);
  if (!new RegExp(`^${ROLE_VALUE_EXPRESSION}$`).test(suffix)) return null;
  return normalized.slice(0, roleIndex).trim() || null;
}

/** 读取显式 `#SKU`，其后只允许角色与一到三个数字因子。 */
function explicitPointSkuName(text: string): string | null {
  const matched = text.trim().match(
    /^#(.+?)\s+(?:(?:学生|老师|教师|导游|司机)\s+)?\d+(?:\.\d+)?(?:\s*(?:人|次)?\s*[xX×*]\s*\d+(?:\.\d+)?){0,2}\s*元?$/u,
  );
  return matched?.[1]?.normalize("NFKC").replace(/\s+/gu, " ").trim() || null;
}

/** 自由 SKU 未显式写角色时，按名称识别专属角色，其余按学生票处理。 */
function pointRoleFromSkuName(skuName: string): string {
  if (/老师|教师/u.test(skuName)) return "老师";
  if (/导游/u.test(skuName)) return "导游";
  if (/司机/u.test(skuName)) return "司机";
  return "学生";
}

/** 把录入中文角色映射为考察点 SKU 主数据角色。 */
function pointRoleCode(role: string | null): string | null {
  if (role === "学生") return "student";
  if (role === "老师" || role === "教师") return "teacher";
  if (role === "导游") return "guide";
  if (role === "司机") return "driver";
  return null;
}

/** 按后端公共解析相同口径规范 SKU 名称，用于候选精确匹配。 */
function normalizedPointSkuName(value: string): string {
  return value.normalize("NFKC").replace(/\s+/gu, " ").trim().toLocaleLowerCase("zh-CN");
}

/** 识别餐别。 */
function detectMealType(text: string): string | null {
  return text.match(/早餐|中餐|晚餐|正餐/)?.[0] ?? null;
}

/** 读取首个 token 并去除 Mentions 前导符。 */
function firstToken(text: string): string {
  return (text.split(/\s+/)[0] ?? "").replace(/^@/, "").trim();
}

/** 仅为兼容的“班线角色”写法移除紧贴角色，其他项目名保持原样。 */
function itemNameToken(
  text: string,
  role: string | null,
  resourceType: ItineraryResourceType,
): string {
  const fallback = firstToken(text);
  if (!role || resourceType !== "包车") return fallback;
  const inlineRole = text.replace(/^@/, "").match(
    /^(班线)(学生|老师|导游|司机)(?=\s*(?:[xX×*]\s*)?\d)/,
  );
  return inlineRole?.[2] === role ? inlineRole[1] : fallback;
}

/** 项目名称内的“3人保险”“第2天保险”不是数量公式，避免与后续计量因子混淆。 */
function quantityFormulaText(text: string): string {
  const nameToken = text.match(/^@?\S+/u)?.[0] ?? "";
  if (!nameToken) return text;
  const formulaSafeName = nameToken
    .replace(/(?:第)?\d+\s*(?:人|晚|天|餐|辆|座)(?![xX]\s*\d)(?=\p{L})/gu, "")
    .replace(/\d+\s*个?\s*项目(?=\p{L})/gu, "");
  return `${formulaSafeName}${text.slice(nameToken.length)}`;
}

/** 住宿预算行展示名统一补“住宿”，已带后缀时不重复追加。 */
function stayItemName(name: string): string {
  return name.endsWith("住宿") ? name : `${name}住宿`;
}


type BusFleetItem = {
  seatCount: number;
  vehicleCount: number;
  unitPrice: number;
  unitPriceText: string;
};

/** 精确汇总车型数量与两位小数单价，避免多车型合计经过浮点数丢分。 */
function busFleetTotalText(
  fleets: ReadonlyArray<Pick<BusFleetItem, "vehicleCount" | "unitPriceText">>,
): string | null {
  let cents = 0n;
  for (const fleet of fleets) {
    if (!Number.isSafeInteger(fleet.vehicleCount) || fleet.vehicleCount <= 0) return null;
    const matched = fleet.unitPriceText.match(/^(\d+)(?:\.(\d{1,2}))?$/);
    if (!matched) return null;
    const priceCents = BigInt(matched[1]) * 100n
      + BigInt(`${matched[2] ?? ""}00`.slice(0, 2));
    cents += priceCents * BigInt(fleet.vehicleCount);
  }
  return `${cents / 100n}.${String(cents % 100n).padStart(2, "0")}`
    .replace(/\.00$/, "")
    .replace(/(\.\d)0$/, "$1");
}

/** 包车车型数值必须是有限正数，并保持在 JavaScript 安全整数范围内。 */
function isValidBusFleetNumber(value: number): boolean {
  return Number.isFinite(value) && value > 0 && value <= Number.MAX_SAFE_INTEGER;
}

/** 统一校验标准包车车型的座位数、车辆数和单价。 */
function isValidBusFleetItem(item: BusFleetItem): boolean {
  return isValidBusFleetNumber(item.seatCount)
    && isValidBusFleetNumber(item.vehicleCount)
    && isValidBusFleetNumber(item.unitPrice);
}

/** 校验逗号分隔列表已被解析器完整消费，避免局部成功后静默丢项。 */
function isFullyConsumedList(source: string, matches: RegExpMatchArray[]): boolean {
  if (!matches.length) return false;
  let cursor = 0;
  for (const [index, match] of matches.entries()) {
    const start = match.index ?? 0;
    const gap = source.slice(cursor, start);
    if (index === 0 ? gap.trim() !== "" : !/^\s*[,，]\s*$/.test(gap)) return false;
    cursor = start + match[0].length;
  }
  return source.slice(cursor).trim() === "";
}

/** 解析“地名 车队2x1200”这类不含座位数的快捷包车输入。 */
function parseSimpleBusRecord(rawText: string, context: ItineraryResourceParseContext): ItineraryResourceEntriesParseResult {
  const sourceText = rawText.trim();
  const text = normalizeItineraryResourceNumericText(sourceText);
  const header = text.match(/^(.*?)\s*车队\s*(.+)$/);
  if (!header) {
    const result = failure("bus_header_invalid", "vehicleSpec", "车队记录需包含车辆数和单价");
    if (result.ok) throw new Error("unreachable");
    return result;
  }

  const location = header[1].trim();
  const fleetText = header[2].trim();
  const fleetPattern = /(\d+)\s*(?:[xX×*]\s*|辆\s*(?:[xX×*]\s*)?)(\d+(?:\.\d+)?)/g;
  const fleetMatches = [...fleetText.matchAll(fleetPattern)];
  if (!fleetMatches.length) {
    const result = failure("bus_fleet_required", "vehicleSpec", "请使用“车队2x1200”录入车辆数和单价");
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  if (!isFullyConsumedList(fleetText, fleetMatches)) {
    const result = failure("bus_fleet_invalid", "vehicleSpec", "车队价格列表存在无法识别的内容，请检查每组车辆数和单价");
    if (result.ok) throw new Error("unreachable");
    return result;
  }

  const fleets = fleetMatches.map((match) => ({
    seatCount: 0,
    vehicleCount: Number(match[1]),
    unitPrice: Number(match[2]),
    unitPriceText: match[2],
  }));
  if (fleets.some((fleet) => (
    !isValidBusFleetNumber(fleet.vehicleCount)
    || !isValidBusFleetNumber(fleet.unitPrice)
  ))) {
    const result = failure(
      "bus_fleet_invalid",
      "vehicleSpec",
      "车辆数和单价必须为大于 0 的有限数值，且不得超过安全整数范围",
    );
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  const singleFleet = fleets.length === 1;
  const total = fleets.reduce((sum, fleet) => sum + fleet.vehicleCount * fleet.unitPrice, 0);
  const totalText = busFleetTotalText(fleets) ?? String(total);
  return {
    ok: true,
    previews: [{
      resourceType: context.activeResourceType,
      resourceEntryType: "包车",
      subtype: "包车",
      itemName: location ? `${location} 包车` : "包车",
      spec: fleets.map((fleet) => `${fleet.vehicleCount}辆 x ${fleet.unitPriceText}元`).join(" + "),
      unit: singleFleet ? "辆" : "车趟",
      travelerRole: null,
      mealType: null,
      mealCount: null,
      quantity: singleFleet ? fleets[0].vehicleCount : 1,
      unitPrice: singleFleet ? fleets[0].unitPrice : total,
      unitPriceText: singleFleet ? fleets[0].unitPriceText : totalText,
      nightCount: null,
      dayCount: null,
      priceSourceRef: `manual_bus:local_charter:${location || "all"}`,
      isLinkedToPeopleCount: false,
      sourceText,
    }],
  };
}

/** 解析包车记录中的日号、服务类型和一个或多个车型价格。 */
function parseBusRecord(rawText: string, context?: ItineraryResourceParseContext): ItineraryResourceEntriesParseResult {
  const sourceText = rawText.trim();
  const text = normalizeItineraryResourceNumericText(sourceText);
  const header = text.match(/^第?\s*(\d+(?:\s*-\s*\d+|\s*[,，]\s*\d+)?)(?:\s*天)?\s*(.*?)\s*(往返接送|接送|单送|单接|包车)\s*/);
  if (!header) {
    const result = failure("bus_header_invalid", "dayScope", "包车记录需以日号和服务类型开头");
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  const dayScope = header[1].replace(/\s+/g, "").replace(/，/g, ",");
  const dayNumbers = dayScope.split(/[-,]/).map(Number);
  if (dayNumbers.some((day) => !Number.isSafeInteger(day) || day <= 0)) {
    const result = failure(
      "numeric_value_out_of_range",
      "dayScope",
      "包车日号超出可安全计算范围，请缩小日号",
      dayScope,
    );
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  const location = header[2].trim();
  const serviceText = header[3];
  const fleetText = text.slice(header[0].length).trim();
  const fleets: BusFleetItem[] = [];
  const fleetPattern = /(\d+)\s*座\s*(\d+)\s*(?:[xX×*]|辆\s*[xX×*]?)\s*(\d+(?:\.\d+)?)/g;
  const fleetMatches = [...fleetText.matchAll(fleetPattern)];
  for (const match of fleetMatches) {
    fleets.push({
      seatCount: Number(match[1]),
      vehicleCount: Number(match[2]),
      unitPrice: Number(match[3]),
      unitPriceText: match[3],
    });
  }
  if (!fleets.length) {
    const result = failure("bus_fleet_required", "vehicleSpec", "请使用“39座 2x1200”录入车型、车辆数和单价");
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  if (!isFullyConsumedList(fleetText, fleetMatches)) {
    const result = failure("bus_fleet_invalid", "vehicleSpec", "车型列表存在无法识别的内容，请检查每个车型的车辆数和单价");
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  if (fleets.some((fleet) => !isValidBusFleetItem(fleet))) {
    const result = failure(
      "bus_fleet_invalid",
      "vehicleSpec",
      "座位数、车辆数和单价必须为大于 0 的有限数值，且不得超过安全整数范围",
    );
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  const spec = fleets.map((fleet) => `${fleet.seatCount}座 x ${fleet.vehicleCount}辆`).join(" + ");
  const total = fleets.reduce((sum, fleet) => sum + fleet.vehicleCount * fleet.unitPrice, 0);
  const totalText = busFleetTotalText(fleets) ?? String(total);
  const range = dayScope.match(/^(\d+)-(\d+)$/);
  const dayCount = range ? Number(range[2]) - Number(range[1]) + 1 : null;
  if (dayCount !== null && dayCount <= 0) {
    const result = failure("bus_day_scope_invalid", "dayScope", "包车结束日不能早于开始日", dayScope);
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  const dayLabel = dayScope.includes("-")
    ? `D${dayScope.replace("-", "-D")}`
    : dayScope.includes(",")
      ? dayScope.split(",").map((day) => `D${day}`).join(",")
      : `D${dayScope}`;

  /** 构造一条手工包车预览；多车型时整条记录按车型金额汇总。 */
  const buildBusPreview = (
    entryType: string,
    serviceCode: string,
    itemDayLabel: string,
    sourceDayScope: string,
    previewDayCount: number | null,
  ): ItineraryResourcePreview => {
    const singlePackageFleet = entryType === "包车" && fleets.length === 1;
    const namedDayLabel = location ? `${itemDayLabel} ${location}` : itemDayLabel;
    return {
      resourceType: context?.activeResourceType || "包车",
      resourceEntryType: entryType,
      subtype: entryType,
      itemName: `${namedDayLabel} ${entryType}`,
      spec,
      unit: singlePackageFleet ? "辆" : "车趟",
      travelerRole: null,
      mealType: null,
      mealCount: null,
      quantity: singlePackageFleet ? fleets[0].vehicleCount : 1,
      unitPrice: singlePackageFleet ? fleets[0].unitPrice : total,
      unitPriceText: singlePackageFleet ? fleets[0].unitPriceText : totalText,
      nightCount: null,
      dayCount: previewDayCount,
      priceSourceRef: `manual_bus:${serviceCode}:${sourceDayScope}`,
      isLinkedToPeopleCount: false,
      sourceText,
    };
  };

  if (serviceText === "接送" || serviceText === "往返接送") {
    const roundTransferDays = dayScope.match(/^(\d+),(\d+)$/);
    if (!roundTransferDays) {
      const result = failure(
        "bus_round_transfer_days_invalid",
        "dayScope",
        "接送需使用两个离散日号，例如“1,14 接送”",
        dayScope,
      );
      if (result.ok) throw new Error("unreachable");
      return result;
    }
    const dropoffDay = roundTransferDays[1];
    const pickupDay = roundTransferDays[2];
    return {
      ok: true,
      previews: [
        buildBusPreview("单送", "dropoff", `D${dropoffDay}`, dropoffDay, null),
        buildBusPreview("单接", "pickup", `D${pickupDay}`, pickupDay, null),
      ],
    };
  }

  const serviceCode = serviceText === "单送"
    ? "dropoff"
    : serviceText === "单接"
      ? "pickup"
      : "local_charter";
  return {
    ok: true,
    previews: [buildBusPreview(serviceText, serviceCode, dayLabel, dayScope, dayCount)],
  };
}

/** 把同一路线中的多个角色价格拆成独立的大交通预算行。 */
function parseTransportRecord(rawText: string, context: ItineraryResourceParseContext): ItineraryResourceEntriesParseResult {
  const sourceText = rawText.trim();
  const text = normalizeItineraryResourceNumericText(sourceText);
  const firstRoleIndex = text.search(/\s+(?=学生|老师|导游|司机)/);
  if (firstRoleIndex < 0) {
    const result = failure("transport_role_required", "travelerRole", "无法识别大交通角色，请输入学生、老师、导游或司机");
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  const transport = findStructuredTransport(text, context)
    ?? parseTransport(text.slice(0, firstRoleIndex).trim());
  if (!transport.ok) {
    const result = transport.result;
    if (result.ok) throw new Error("unreachable");
    return result;
  }

  const roleText = text.slice(firstRoleIndex).trim();
  const previews: ItineraryResourcePreview[] = [];
  const rolePattern = /(学生|老师|导游|司机)\s*(?:[xX×*]\s*)?(?:(\d+)\s*(?:人\s*(?:[xX×*]\s*)?|[xX×*]\s*))?(\d+(?:\.\d+)?)(?=\s*(?:[,，]|$))/g;
  const roleMatches = [...roleText.matchAll(rolePattern)];
  for (const match of roleMatches) {
    const role = match[1];
    const explicitPeople = match[2] ? Number(match[2]) : null;
    previews.push({
      resourceType: context.activeResourceType,
      resourceEntryType: transport.entryType,
      subtype: transport.entryType,
      itemName: transportItemName(transport),
      spec: transport.seatClass || detectSpec(text, context.activeResourceType, role),
      unit: "人次",
      travelerRole: role,
      mealType: null,
      mealCount: null,
      quantity: explicitPeople ?? 1,
      unitPrice: Number(match[3]),
      unitPriceText: match[3],
      nightCount: null,
      dayCount: null,
      isLinkedToPeopleCount: explicitPeople === null,
      manualPeopleCount: explicitPeople,
      sourceText,
      transportMode: transport.mode,
      fromPoint: transport.from,
      toPoint: transport.to,
      routeCode: transport.routeCode,
      seatClass: transport.seatClass,
    });
  }
  if (previews.length && isFullyConsumedList(roleText, roleMatches)) return { ok: true, previews };
  if (previews.length) {
    const result = failure("transport_role_price_invalid", "unitPrice", "角色价格列表存在无法识别的内容，请检查每个角色的价格");
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  const result = failure("transport_role_price_required", "unitPrice", "无法识别大交通角色价格");
  if (result.ok) throw new Error("unreachable");
  return result;
}

/** 判断住宿文本是否使用角色与晚数口径。 */
function isStayRoleRecord(text: string): boolean {
  return /(学生|老师|导游|司机).*\d+\s*晚/.test(text);
}

/** 把同一住宿区域中的多个角色拆成独立预算行。 */
function parseStayRecord(rawText: string, context: ItineraryResourceParseContext): ItineraryResourceEntriesParseResult {
  const sourceText = rawText.trim();
  const text = normalizeItineraryResourceNumericText(sourceText);
  const option = [...context.entryOptions.stayAreas]
    .sort((left, right) => right.name.length - left.name.length)
    .find((item) => text.replace(/^@/, "").startsWith(item.name));
  if (!option) {
    const value = firstToken(text).replace(/住宿$/, "");
    const result = failure(
      "entry_option_not_found",
      "stayArea",
      `未找到行程住宿区域“${value}”，请从 @ 候选中选择`,
      value,
      context.entryOptions.stayAreas.map((item) => item.name),
    );
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  const roleText = text
    .replace(/^@/, "")
    .slice(option.name.length)
    .replace(/^住宿\s*/, "")
    .trim();
  const previews: ItineraryResourcePreview[] = [];
  const rolePattern = /(学生|老师|导游|司机)\s*(?:[xX×*]\s*)?(?:(?:商务连锁|舒适型|舒适|高标|四星|五星|豪华|度假)(?:标准)?\s*)?(?:(\d+)\s*人\s*(?:[xX×*]\s*)?)?(\d+)\s*晚\s*(?:(?:(\d+)\s*)?[xX×*]\s*)?(\d+(?:\.\d+)?)\s*(?:元)?(?=\s*(?:[,，]|$))/g;
  const roleMatches = [...roleText.matchAll(rolePattern)];
  for (const match of roleMatches) {
    if (match[2] && match[4]) {
      const result = failure(
        "stay_people_count_conflict",
        "manualPeopleCount",
        "住宿人数不能同时使用“人数”和“人数 x 单价”两种写法",
      );
      if (result.ok) throw new Error("unreachable");
      return result;
    }
    const role = match[1];
    const nights = Number(match[3]);
    const explicitPeopleText = match[2] ?? match[4];
    const explicitPeople = explicitPeopleText ? Number(explicitPeopleText) : null;
    previews.push({
      resourceType: "住宿",
      subtype: "住宿预算",
      itemName: stayItemName(option.name),
      spec: detectSpec(match[0], "住宿", role),
      unit: "人晚",
      travelerRole: role,
      mealType: null,
      mealCount: null,
      quantity: (explicitPeople ?? 1) * nights,
      unitPrice: Number(match[5]),
      unitPriceText: match[5],
      nightCount: nights,
      dayCount: null,
      isLinkedToPeopleCount: explicitPeople === null,
      manualPeopleCount: explicitPeople,
      sourceText,
      spuTableName: "dim_stay_area",
      spuId: option.id,
      spuName: option.name,
    });
  }
  if (previews.length && isFullyConsumedList(roleText, roleMatches)) {
    return { ok: true, previews };
  }
  if (previews.length) {
    const result = failure(
      "stay_role_price_invalid",
      "travelerRole",
      "住宿角色价格列表存在无法识别的内容，请检查每个角色、晚数和价格",
    );
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  const result = failure("stay_role_required", "travelerRole", "无法识别住宿角色，请输入学生、老师、导游或司机");
  if (result.ok) throw new Error("unreachable");
  return result;
}

/** 把同一杂费项目中的多个角色价格原子拆成独立预算行。 */
function parseMiscRoleRecord(rawText: string, context: ItineraryResourceParseContext): ItineraryResourceEntriesParseResult {
  const sourceText = rawText.trim();
  const text = normalizeItineraryResourceNumericText(sourceText);
  const firstRoleIndex = text.search(/\s+(?=学生|老师|导游|司机)/);
  if (firstRoleIndex < 0) {
    const result = failure("misc_role_required", "travelerRole", "无法识别杂费角色，请输入学生、老师、导游或司机");
    if (result.ok) throw new Error("unreachable");
    return result;
  }

  const itemName = text.slice(0, firstRoleIndex).replace(/^@/, "").trim();
  const roleText = text.slice(firstRoleIndex).trim();
  const rolePattern = /(学生|老师|导游|司机)\s*(?:[xX×*]\s*)?(?:(\d+)\s*人\s*(?:[xX×*]\s*)?)?(?:(\d+)\s*天\s*(?:[xX×*]\s*)?)?(\d+(?:\.\d+)?)\s*(?:元)?(?=\s*(?:[,，]|$))/g;
  const roleMatches = [...roleText.matchAll(rolePattern)];
  const previews = roleMatches.map((match): ItineraryResourcePreview => {
    const people = match[2] ? Number(match[2]) : null;
    const days = match[3] ? Number(match[3]) : null;
    return {
      resourceType: context.activeResourceType,
      subtype: "人头杂费",
      itemName,
      spec: match[1],
      unit: days === null ? "人" : "人天",
      travelerRole: match[1],
      mealType: null,
      mealCount: null,
      quantity: (people ?? 1) * (days ?? 1),
      unitPrice: Number(match[4]),
      unitPriceText: match[4],
      nightCount: null,
      dayCount: days,
      isLinkedToPeopleCount: people === null,
      manualPeopleCount: people,
      sourceText,
    };
  });
  if (previews.length && isFullyConsumedList(roleText, roleMatches)) return { ok: true, previews };

  const result = failure(
    "misc_role_price_invalid",
    "unitPrice",
    "杂费角色价格列表存在无法识别的内容，请检查每个角色的价格",
  );
  if (result.ok) throw new Error("unreachable");
  return result;
}

type PointRolePriceFormula = {
  quantity: number;
  billingCount: number;
  unitPrice: number;
  manualPeopleCount: number | null;
};

/** 解析考察点角色后的单价公式，支持“40”“1x80”和“1x2x80”。 */
function parsePointRolePriceFormula(text: string): PointRolePriceFormula | null {
  const normalized = text.trim().replace(/元$/u, "").trim();
  const triple = normalized.match(/^(\d+(?:\.\d+)?)\s*(?:人\s*)?[xX×*]\s*(\d+(?:\.\d+)?)\s*(?:次\s*)?[xX×*]\s*(\d+(?:\.\d+)?)$/u);
  if (triple) {
    const people = Number(triple[1]);
    const billingCount = Number(triple[2]);
    const unitPrice = Number(triple[3]);
    return { quantity: people * billingCount, billingCount, unitPrice, manualPeopleCount: people };
  }
  const pair = normalized.match(/^(\d+(?:\.\d+)?)\s*(?:人\s*)?[xX×*]\s*(\d+(?:\.\d+)?)$/u);
  if (pair) {
    const people = Number(pair[1]);
    return { quantity: people, billingCount: 1, unitPrice: Number(pair[2]), manualPeopleCount: people };
  }
  const price = normalized.match(/^\d+(?:\.\d+)?$/u);
  if (!price) return null;
  return { quantity: 1, billingCount: 1, unitPrice: Number(price[0]), manualPeopleCount: null };
}

/** 按点位、项目标签和角色拆分考察点批量价格，支持同角色用“+”列出多个票价。 */
function parsePointRoleRecord(rawText: string, context: ItineraryResourceParseContext): ItineraryResourceEntriesParseResult {
  const sourceText = rawText.trim();
  const text = normalizeItineraryResourceNumericText(sourceText);
  const point = [...context.entryOptions.points]
    .sort((left, right) => right.name.length - left.name.length)
    .find((candidate) => text.replace(/^@/u, "").startsWith(candidate.name));
  if (!point) {
    const value = firstToken(text);
    const result = failure(
      "entry_option_not_found",
      "point",
      `未找到行程考察点“${value}”，请从 @ 候选中选择`,
      value,
      context.entryOptions.points.map((candidate) => candidate.name),
    );
    if (result.ok) throw new Error("unreachable");
    return result;
  }

  const rawPointDetail = text.replace(/^@/u, "").slice(point.name.length).trim();
  const usesSkuName = /^#.+?\s+(?=(?:(?:学生|老师|教师|导游|司机)\s+)?\d)/u.test(rawPointDetail);
  const pointDetail = rawPointDetail.replace(/^#/u, "").trim();
  const firstRole = pointDetail.match(/(学生|老师|导游|司机)\s*(?=\d)/u);
  if (!firstRole || firstRole.index === undefined) {
    const result = failure("point_role_required", "travelerRole", "考察点批量价格需要填写学生、老师、导游或司机");
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  const visibleTypeLabel = pointDetail.slice(0, firstRole.index).trim();
  if (!visibleTypeLabel) {
    const result = failure("point_type_required", "resourceEntryType", "考察点批量价格需要填写门票、讲解或其他项目类型");
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  const rolePattern = /(学生|老师|导游|司机)\s*(\d[^,，;；]*)/gu;
  const roleMatches = [...pointDetail.slice(firstRole.index).matchAll(rolePattern)];
  if (!roleMatches.length || roleMatches[0].index !== 0) {
    const result = failure("point_role_price_invalid", "unitPrice", "考察点角色价格列表无法识别，请检查角色和单价");
    if (result.ok) throw new Error("unreachable");
    return result;
  }
  const roleText = pointDetail.slice(firstRole.index);
  if (!isFullyConsumedList(roleText, roleMatches)) {
    const result = failure("point_role_price_invalid", "unitPrice", "考察点角色价格列表存在无法识别的内容，请检查每个角色的价格");
    if (result.ok) throw new Error("unreachable");
    return result;
  }

  const entryType = detectPointEntryType(visibleTypeLabel);
  const pointItems = point.items ?? [];
  const findPointItem = (role: string) => {
    const itemRole = pointRoleCode(role);
    if (!itemRole) return {};
    if (usesSkuName) {
      const exactNameMatches = pointItems.filter((item) => (
        item.travelerRole === itemRole
        && normalizedPointSkuName(item.skuName) === normalizedPointSkuName(visibleTypeLabel)
      ));
      return exactNameMatches.length === 1 ? { item: exactNameMatches[0] } : {};
    }
    const targetLabel = visibleTypeLabel === "讲解" ? "讲解服务" : visibleTypeLabel;
    const labeledItems = pointItems
      .filter((item) => item.travelerRole === itemRole)
      .map((item) => ({
        item,
        label: pointQuickEntryVisibleTypeLabel(item, pointItems).replace(/^#/u, ""),
      }));
    const exactMatches = labeledItems.filter(({ label }) => label === targetLabel);
    if (exactMatches.length === 1) return { item: exactMatches[0].item };
    const prefixMatches = exactMatches.length
      ? exactMatches
      : labeledItems.filter(({ label }) => label.startsWith(`${targetLabel} `));
    if (prefixMatches.length === 1) return { item: prefixMatches[0].item };
    return prefixMatches.length > 1
      ? { ambiguousCandidates: prefixMatches.map(({ label }) => label) }
      : {};
  };

  const previews: ItineraryResourcePreview[] = [];
  for (const match of roleMatches) {
    const role = match[1];
    const itemMatch = findPointItem(role);
    if (itemMatch.ambiguousCandidates) {
      const result = failure(
        "point_sku_ambiguous",
        "skuId",
        `${point.name}的${role}${visibleTypeLabel}存在多个项目，请选择完整候选名称`,
        visibleTypeLabel,
        itemMatch.ambiguousCandidates,
      );
      if (result.ok) throw new Error("unreachable");
      return result;
    }
    const item = itemMatch.item;
    const resolvedEntryType = item ? pointQuickEntryResourceEntryType(item) : entryType;
    for (const formulaText of match[2].split("+")) {
      const formula = parsePointRolePriceFormula(formulaText);
      if (!formula) {
        const result = failure("point_role_price_invalid", "unitPrice", `无法识别${role}价格“${formulaText.trim()}”`, formulaText.trim());
        if (result.ok) throw new Error("unreachable");
        return result;
      }
      previews.push({
        resourceType: context.activeResourceType,
        resourceEntryType: resolvedEntryType,
        subtype: resolvedEntryType,
        itemName: resolvedEntryType === "讲解服务" ? `${point.name}讲解服务` : point.name,
        spec: `${role}${resolvedEntryType}`,
        unit: "人次",
        travelerRole: role,
        mealType: null,
        mealCount: null,
        quantity: formula.quantity,
        unitPrice: formula.unitPrice,
        unitPriceText: String(formula.unitPrice),
        nightCount: null,
        dayCount: null,
        isLinkedToPeopleCount: formula.manualPeopleCount === null,
        manualPeopleCount: formula.manualPeopleCount,
        sourceText,
        spuTableName: "resource_point_spu",
        spuId: point.id,
        spuName: point.name,
        ...(item || usesSkuName ? {
          skuTableName: "resource_point_sku",
          skuId: item?.skuId ?? null,
          skuName: item?.skuName ?? visibleTypeLabel,
        } : {}),
      });
    }
  }
  return previews.length ? { ok: true, previews } : failure("point_role_price_required", "unitPrice", "考察点缺少角色价格");
}
/** 按资源大类派生预览规格。 */
function detectSpec(text: string, resourceType: ItineraryResourceType, role: string | null): string {
  const rolePrefix = role ? `${role} ` : "";
  switch (resourceType) {
    case "住宿": return `${rolePrefix}${/舒适|高标|四星|五星|豪华|度假/.test(text) ? "舒适型标准" : "商务连锁标准"}`;
    case "餐": {
      if (/特色|欢迎|宴|风味|品鉴/.test(text)) return `${rolePrefix}特色餐标`;
      return `${rolePrefix}${detectMealType(text) ?? "正餐"}餐标`;
    }
    case "考察点": return `${rolePrefix}${detectPointEntryType(text)}`;
    case "包车": return role
      ? `${role}${/[1-9]\d*\s*人/.test(text) ? "固定人数" : "人数联动"}`
      : text.match(/(\d+)\s*座/)
        ? `${text.match(/(\d+)\s*座/)?.[1]}座整车`
        : "整车包车";
    case "大交通": return text.match(/硬卧|软卧|二等座|一等座|商务座|经济舱|高铁|动车|飞机|火车/)?.[0] ?? "大交通";
    case "导游": return role ? `${role}摊销` : "服务摊销";
    default: return role ?? "手工识别";
  }
}

/** 解析完成的大交通身份；快捷项额外保留可见前缀供价格提取排除车次数字。 */
type ParsedTransport = {
  ok: true;
  entryType: "飞机" | "火车";
  mode: "air" | "rail";
  from: string;
  to: string;
  routeCode?: string;
  seatClass?: string;
  quickEntryPrefix?: string;
};

/** 解析并校验手工大交通类型与单一 `-` 路线。 */
function parseTransport(text: string):
  | ParsedTransport
  | { ok: false; result: ItineraryResourceEntryParseResult } {
  const type = text.match(/^(飞机|火车|高铁)(?:\s+|$)/)?.[1];
  if (!type) {
    return { ok: false, result: failure("transport_type_required", "transportType", "大交通缺少交通类型，请输入飞机、火车或高铁") };
  }
  let route = text
    .slice(type.length)
    .replace(/\s*\d+(?:\.\d+)?\s*(?:元)?\s*$/, "")
    .replace(/\s*(学生|老师|导游|司机)(?:\s*(?:\d+\s*(?:人\s*(?:[xX×*]\s*)?|[xX×*])|[xX×*]))?\s*$/, "")
    .trim();
  let routeCode: string | undefined;
  let seatClass: string | undefined;
  if (type !== "飞机") {
    const seatClassMatch = route.match(RAIL_SEAT_CLASS_SUFFIX_PATTERN);
    if (seatClassMatch) {
      seatClass = seatClassMatch[1];
      route = route.slice(0, seatClassMatch.index).trim();
    }
    const routeCodeMatch = route.match(RAIL_ROUTE_CODE_SUFFIX_PATTERN);
    if (routeCodeMatch) {
      routeCode = routeCodeMatch[1];
      route = route.slice(0, routeCodeMatch.index).trim();
    }
  }
  const separatorCount = route.match(/-/g)?.length ?? 0;
  if (separatorCount === 0) {
    return { ok: false, result: failure("route_separator_required", "route", "大交通路线必须使用一个 - 分隔起终点", route) };
  }
  if (separatorCount !== 1) {
    return { ok: false, result: failure("route_separator_invalid", "route", "大交通路线只能包含一个 - 分隔符", route) };
  }
  const [from, to] = route.split(/\s*-\s*/).map((item) => item.replace(/^@/, "").trim());
  if (!from) {
    return { ok: false, result: failure("transport_endpoint_required", "fromPoint", "大交通路线缺少出发点", from) };
  }
  if (!to) {
    return { ok: false, result: failure("transport_endpoint_required", "toPoint", "大交通路线缺少到达点", to) };
  }
  return {
    ok: true,
    entryType: type === "飞机" ? "飞机" : "火车",
    mode: type === "飞机" ? "air" : "rail",
    from,
    to,
    routeCode,
    seatClass,
  };
}

/** 从动态快捷项可见前缀精确回绑当前行程的车次与席别。 */
function findStructuredTransport(
  text: string,
  context?: ItineraryResourceParseContext,
): ParsedTransport | null {
  if (context?.activeResourceType !== "大交通") return null;
  const normalized = text.trim().replace(/\s+/g, " ");
  const candidate = [...(context.entryOptions.transports ?? [])]
    .map((item) => ({ item, prefix: transportQuickEntryPrefix(item) }))
    .sort((left, right) => right.prefix.length - left.prefix.length)
    .find(({ prefix }) => normalized === prefix || normalized.startsWith(`${prefix} `));
  if (!candidate) return null;
  return {
    ok: true,
    entryType: candidate.item.mode === "air" ? "飞机" : "火车",
    mode: candidate.item.mode,
    from: candidate.item.fromName,
    to: candidate.item.toName,
    routeCode: candidate.item.routeCode.trim() || undefined,
    seatClass: candidate.item.seatClass.trim() || undefined,
    quickEntryPrefix: candidate.prefix,
  };
}

/** 预算行名称保存路线与车次，席别单独保存在规格快照。 */
function transportItemName(
  transport: ParsedTransport,
): string {
  return `${transport.from}-${transport.to}${transport.routeCode ? ` ${transport.routeCode}` : ""}`;
}

/** 解析路线人数联动手续费，并兼容历史“人数 x 次数 x 单价”公式。 */
function parseTransportFee(
  text: string,
  sourceText: string,
  context: ItineraryResourceParseContext,
): ItineraryResourceEntryParseResult | null {
  if (!/手续费/.test(text)) return null;

  const legacyPrefix = /^(?:(飞机|火车|高铁)\s+)?手续费(?:\s|$)/;
  if (legacyPrefix.test(text)) {
    if (!/^(飞机|火车|高铁)\s+手续费(?:\s|$)/.test(text)) {
      return failure(
        "transport_fee_type_required",
        "transportType",
        "手续费缺少交通类型，请以火车、高铁或飞机开头",
      );
    }
    const legacy = text.match(
      /^(飞机|火车|高铁)\s+手续费\s*(?:(学生|老师|导游|司机)\s*)?([1-9]\d*)\s*(?:人\s*)?[xX×*]\s*([1-9]\d*)\s*(?:次\s*)?[xX×*]\s*(\d+(?:\.\d+)?)\s*(?:元)?$/,
    );
    if (legacy) {
      const transportType = legacy[1];
      const role = legacy[2] ?? "学生";
      const people = Number(legacy[3]);
      const times = Number(legacy[4]);
      return {
        ok: true,
        preview: {
          resourceType: "大交通",
          resourceEntryType: "手续费",
          subtype: "手续费",
          itemName: `${transportType}手续费`,
          spec: `${times}次`,
          unit: "人次",
          travelerRole: role,
          mealType: null,
          mealCount: null,
          quantity: people * times,
          unitPrice: Number(legacy[5]),
          unitPriceText: legacy[5],
          nightCount: null,
          dayCount: null,
          isLinkedToPeopleCount: false,
          manualPeopleCount: people,
          sourceText,
        },
      };
    }
  }

  const routeFee = text.match(
    /^(.*?)\s+(学生|老师|导游|司机)\s+手续费\s*(\d+(?:\.\d+)?)\s*(?:元)?$/,
  );
  if (!routeFee) {
    return failure(
      "transport_fee_formula_invalid",
      "quantity",
      "手续费请使用“路线 角色 手续费 单价”录入，例如“火车 太原站-银川站 学生 手续费 5”",
    );
  }
  const transportText = routeFee[1].trim();
  const transport = findStructuredTransport(transportText, context)
    ?? parseTransport(transportText);
  if (!transport.ok) return transport.result;
  return {
    ok: true,
    preview: {
      resourceType: "大交通",
      resourceEntryType: "手续费",
      subtype: "手续费",
      itemName: `${transport.entryType} ${transportItemName(transport)} 手续费`,
      spec: "1次",
      unit: "人次",
      travelerRole: routeFee[2],
      mealType: null,
      mealCount: null,
      quantity: 1,
      unitPrice: Number(routeFee[3]),
      unitPriceText: routeFee[3],
      nightCount: null,
      dayCount: null,
      isLinkedToPeopleCount: true,
      manualPeopleCount: null,
      sourceText,
      transportMode: transport.mode,
      fromPoint: transport.from,
      toPoint: transport.to,
      routeCode: transport.routeCode,
      seatClass: transport.seatClass,
    },
  };
}

/** 多角色无法按当前语法明确拆行时统一要求用户分别录入。 */
function travelerRoleAmbiguousFailure(): ItineraryResourceEntryParseResult {
  return failure(
    "traveler_role_ambiguous",
    "travelerRole",
    "单条核价只能填写一个角色，请拆分为多条录入",
  );
}

const MAX_ENTRY_ROW_LENGTH = 500;
const MAX_SAFE_CENTS = BigInt(Number.MAX_SAFE_INTEGER);

/** 拒绝超过预算项目名称存储容量的单行输入。 */
function entryTextLengthFailure(text: string): ItineraryResourceEntryParseResult | null {
  return text.length > MAX_ENTRY_ROW_LENGTH
    ? failure("input_too_long", "text", `单行输入不能超过 ${MAX_ENTRY_ROW_LENGTH} 个字符，请精简项目名称或拆分录入`)
    : null;
}

/** 判断数字更接近数量还是单价字段，用于返回可操作的结构化错误。 */
function numericTokenField(text: string, tokenEnd: number): "unitPrice" | "quantity" {
  const suffix = text.slice(tokenEnd);
  return /^\s*(?:人|晚|天|餐|辆|座|个?\s*项目|[xX×*])/u.test(suffix)
    ? "quantity"
    : "unitPrice";
}

/** 把最多两位小数的非负数字字符串精确转换为整数分。 */
function decimalTokenCents(token: string): bigint {
  const [integer, fraction = ""] = token.split(".");
  return BigInt(integer) * 100n + BigInt(`${fraction}00`.slice(0, 2));
}

/** 包车日号和车型列表拥有独立的完整消费校验，不把它们的逗号误判为金额分隔符。 */
function hasStructuredBusCommaSyntax(text: string): boolean {
  return /^\s*(?:第\s*)?\d+(?:\s*(?:-|[,，])\s*\d+)?(?:\s*天)?\s+.*(?:往返接送|接送|单送|单接|包车)/u.test(text)
    || /车队\s*\d+\s*(?:[xX×*]|辆)/u.test(text);
}

/** 判断输入是否为需按日号和车型拆解的标准包车记录。 */
function isStructuredBusRecord(text: string): boolean {
  return /^\s*(?:第\s*)?\d/u.test(text)
    && /往返接送|接送|单送|单接|包车/u.test(text)
    && /\d+\s*座/u.test(text);
}

/** 在进入各场景解析器前统一拒绝负数、畸形分隔、小数越界和分值精度丢失。 */
function numericSyntaxFailure(
  text: string,
  resourceType: ItineraryResourceType,
): ItineraryResourceEntryParseResult | null {
  const structuredBusSyntax = resourceType === "包车" && hasStructuredBusCommaSyntax(text);
  const structuredMealSyntax = resourceType === "餐"
    && /[xX×*]/u.test(text)
    && /[,，]/u.test(text);
  const scientificNotation = text.match(
    /(?:^|[^A-Za-z\d._])[-+]?(?:\d+(?:\.\d*)?|\.\d+)\s*[eE]\s*[+-]?\s*\d+/u,
  );
  if (scientificNotation) {
    return failure(
      "numeric_notation_invalid",
      "unitPrice",
      "不支持科学计数法，请输入普通十进制金额或数量",
    );
  }
  const negativeField = negativeNumericField(text);
  if (negativeField) {
    return failure(
      "numeric_value_negative",
      negativeField,
      negativeField === "unitPrice" ? "金额不能为负数，请输入 0 或正数" : "数量不能为负数，请输入正数",
    );
  }
  const joinedNumeric = !structuredBusSyntax && text.match(
    /\d\s*[-−‐‑‒–—―﹘﹣－]\s*\d+(?:\.\d+)?\s*(?:(元|人|晚|天|餐|辆|座|个?\s*项目)|$)/u,
  );
  if (joinedNumeric) {
    const field = !joinedNumeric[1] || joinedNumeric[1] === "元" ? "unitPrice" : "quantity";
    return failure(
      "number_format_invalid",
      field,
      "数字间不能使用连字符，请分别填写数量与金额",
    );
  }
  const incompleteDecimal = text.match(
    /(?:^|[^\d.])[-+]?\s*(?:\.\d+|\d+\.)\s*(?:(元|人|晚|天|餐|辆|座|个?\s*项目)|$)/u,
  );
  if (incompleteDecimal) {
    const field = !incompleteDecimal[1] || incompleteDecimal[1] === "元" ? "unitPrice" : "quantity";
    return failure(
      "number_format_invalid",
      field,
      "小数必须同时包含整数位和小数位，例如 0.5",
    );
  }
  if (/\d(?:_+\s*\d|['‘’]\s*\d|\.(?:\s*\.)+\s*\d|\.\d+\.\d+)/u.test(text)) {
    return failure(
      "number_format_invalid",
      "unitPrice",
      "金额分隔格式不正确，请输入连续数字并且只保留一个小数点",
    );
  }
  if (
    /\d\s*[,，]\s*\d|\d{1,3}[ \t\u00a0\u202f]+\d{3}(?!\d)/u.test(text)
    && !structuredBusSyntax
    && !structuredMealSyntax
  ) {
    return failure(
      "number_format_invalid",
      "unitPrice",
      "金额分隔格式不正确，请按 1,000 或 1000 输入",
    );
  }
  for (const matched of text.matchAll(/\d+(?:\.\d+)?/g)) {
    const token = matched[0];
    const field = numericTokenField(text, (matched.index ?? 0) + token.length);
    const fraction = token.split(".")[1] ?? "";
    if (fraction.length > 2) {
      return failure(
        "numeric_precision_invalid",
        field,
        field === "unitPrice" ? "金额最多保留两位小数" : "数量最多保留两位小数",
      );
    }
    if (structuredBusSyntax) continue;
    const cents = decimalTokenCents(token);
    if (
      cents > MAX_SAFE_CENTS
    ) {
      return failure(
        "numeric_value_out_of_range",
        field,
        field === "unitPrice" ? "金额超出可安全计算范围，请缩小单价" : "数量超出可安全计算范围，请缩小数量",
      );
    }
  }
  return null;
}

/** 预算金额以两位小数参与运算，限制到可用整数分精确表达的范围。 */
function previewNumericSafetyFailure(
  preview: ItineraryResourcePreview,
): ItineraryResourceEntryParseResult | null {
  const quantityText = String(preview.quantity);
  const unitPriceText = preview.unitPriceText ?? String(preview.unitPrice);
  const quantityMatched = quantityText.match(/^(\d+)(?:\.(\d{1,2}))?$/);
  const unitPriceMatched = unitPriceText.match(/^(\d+)(?:\.(\d{1,2}))?$/);
  const quantityCents = quantityMatched
    ? BigInt(quantityMatched[1]) * 100n + BigInt(`${quantityMatched[2] ?? ""}00`.slice(0, 2))
    : null;
  const unitPriceCents = unitPriceMatched
    ? BigInt(unitPriceMatched[1]) * 100n + BigInt(`${unitPriceMatched[2] ?? ""}00`.slice(0, 2))
    : null;
  if (quantityCents === null || quantityCents <= 0n || quantityCents > MAX_SAFE_CENTS) {
    return failure("numeric_value_out_of_range", "quantity", "数量超出可安全计算范围，请缩小数量");
  }
  if (unitPriceCents === null || unitPriceCents < 0n || unitPriceCents > MAX_SAFE_CENTS) {
    return failure("numeric_value_out_of_range", "unitPrice", "金额超出可安全计算范围，请缩小单价");
  }
  const amountCents = (quantityCents * unitPriceCents + 50n) / 100n;
  if (amountCents > MAX_SAFE_CENTS) {
    return failure("numeric_value_out_of_range", "unitPrice", "预计金额超出可安全计算范围，请缩小数量或单价");
  }
  return null;
}

/** 为单行解析结果追加统一的数值安全校验。 */
function validateSinglePreviewResult(
  result: ItineraryResourceEntryParseResult,
): ItineraryResourceEntryParseResult {
  if (!result.ok) return result;
  return previewNumericSafetyFailure(result.preview) ?? result;
}

/** 规则解析自然语言为预览行，不接 LLM，不直接落行。 */
export function parseItineraryResourceEntry(
  rawText: string,
  context?: ItineraryResourceParseContext,
): ItineraryResourceEntryParseResult {
  const sourceText = rawText.trim();
  const text = normalizeItineraryResourceNumericText(sourceText);
  if (!text) return failure("input_empty", "text", "请输入资源信息");
  const lengthFailure = entryTextLengthFailure(sourceText);
  if (lengthFailure) return lengthFailure;
  const resourceType = context?.activeResourceType || detectResourceType(text);
  const syntaxFailure = numericSyntaxFailure(text, resourceType);
  if (syntaxFailure) return syntaxFailure;
  if (resourceType === "包车" && isStructuredBusRecord(text)) {
    const busResult = parseBusRecord(sourceText, context);
    if (!busResult.ok) return busResult;
    if (busResult.previews.length !== 1) {
      return failure("bus_multiple_entries_requires_batch", "text", "接送记录会生成多条明细，请使用批量录入");
    }
    return validateSinglePreviewResult({ ok: true, preview: busResult.previews[0] });
  }
  const managedTaxItemName = text
    .replace(/^@/, "")
    .match(/^(营业税)(?=$|[^\p{L}])/u)?.[1];
  if (managedTaxItemName) {
    return failure(
      "business_tax_managed_by_system",
      "resourceEntryType",
      `${managedTaxItemName}由系统自动计算，无需手工录入`,
    );
  }

  if (context?.activeResourceType === "大交通") {
    const transportFee = parseTransportFee(text, sourceText, context);
    if (transportFee) return validateSinglePreviewResult(transportFee);
  }

  if (context?.activeResourceType === "住宿" && !/[,，]/.test(text) && isStayRoleRecord(text)) {
    const stayResult = parseStayRecord(sourceText, context);
    if (!stayResult.ok) return stayResult;
    return validateSinglePreviewResult({ ok: true, preview: stayResult.previews[0] });
  }

  const structuredTransport = findStructuredTransport(text, context);
  const normalizedText = text.trim().replace(/\s+/g, " ");
  const priceText = structuredTransport?.quickEntryPrefix
    ? normalizedText.slice(structuredTransport.quickEntryPrefix.length).trim()
    : /^(?:火车|高铁)(?:\s+|$)/u.test(text)
      ? withoutRailRouteCode(text)
      : text;
  const multiplicativeFormula = resourceType === "考察点" || resourceType === "餐"
    ? parseMultiplicativePriceFormula(priceText)
    : null;
  const price = multiplicativeFormula
    ? { value: multiplicativeFormula.unitPrice, text: multiplicativeFormula.unitPriceText }
    : extractPrice(priceText);
  if (price === null) return failure("price_required", "unitPrice", "请按格式示例补充价格");

  const roleMentions = separatedRoleMatches(text);
  const compatibleInlineRole = inlineBusRole(text, resourceType);
  const attachedRoleMentions = attachedRoleValueMatches(text, resourceType);
  if (roleMentions.length + attachedRoleMentions.length + (compatibleInlineRole ? 1 : 0) > 1) {
    return travelerRoleAmbiguousFailure();
  }
  let role = detectRoleNearValue(text, resourceType)
    ?? (resourceType === "杂费" ? null : detectRole(text, resourceType));
  let itemName = itemNameToken(text, role, resourceType) || resourceType;
  if (resourceType === "杂费" && /^开票税金(?=$|[^\p{L}])/u.test(text)) {
    itemName = "开票税金";
  }
  let resourceEntryType: string | undefined;
  let spuTableName: string | undefined;
  let spuId: string | null | undefined;
  let spuName: string | undefined;
  let skuTableName: string | undefined;
  let skuId: string | null | undefined;
  let skuName: string | undefined;
  let transport: ReturnType<typeof parseTransport> | undefined;

  if (resourceType === "住宿" || resourceType === "餐") {
    const options = context?.entryOptions.stayAreas;
    itemName = itemName.replace(/住宿$/, "");
    if (options) {
      const normalizedStayText = text.replace(/^@/, "").trim();
      const option = [...options]
        .sort((left, right) => right.name.length - left.name.length)
        .find((item) => {
          const candidateNames = resourceType === "住宿"
            ? [stayItemName(item.name), item.name]
            : [item.name];
          return candidateNames.some((name) => (
            normalizedStayText === name || normalizedStayText.startsWith(`${name} `)
          ));
        });
      if (!option) {
        return failure("entry_option_not_found", "stayArea", `未找到行程住宿区域“${itemName}”，请从 @ 候选中选择`, itemName, options.map((item) => item.name));
      }
      itemName = resourceType === "住宿" ? stayItemName(option.name) : option.name;
      spuTableName = "dim_stay_area";
      spuId = option.id;
      spuName = option.name;
    }
  } else if (resourceType === "考察点") {
    resourceEntryType = detectPointEntryType(text);
    const options = context?.entryOptions.points;
    if (options) {
      const normalizedPointText = text.replace(/^@/, "").trim();
      const option = [...options]
        .sort((left, right) => right.name.length - left.name.length)
        .find((item) => normalizedPointText.startsWith(item.name));
      if (!option) {
        return failure("entry_option_not_found", "point", `未找到行程考察点“${itemName}”，请从 @ 候选中选择`, itemName, options.map((item) => item.name));
      }
      const pointDetail = normalizedPointText.slice(option.name.length);
      const freeSkuName = explicitPointSkuName(pointDetail);
      if (freeSkuName && !role) role = pointRoleFromSkuName(freeSkuName);
      const itemRole = pointRoleCode(role);
      const visibleTypeLabel = freeSkuName ?? pointVisibleLabelBeforeRole(pointDetail, role);
      const matchingPointItems = itemRole && visibleTypeLabel
        ? option.items?.filter((item) => (
          item.travelerRole === itemRole
          && (freeSkuName
            ? normalizedPointSkuName(item.skuName) === normalizedPointSkuName(freeSkuName)
            : pointQuickEntryVisibleTypeLabel(item, option.items ?? []) === visibleTypeLabel)
        )) ?? []
        : [];
      const pointItem = matchingPointItems.length === 1 ? matchingPointItems[0] : undefined;
      resourceEntryType = pointItem
        ? pointQuickEntryResourceEntryType(pointItem)
        : detectPointEntryType(pointDetail);
      itemName = /耳麦/.test(pointDetail)
        ? `${option.name}耳麦`
        : resourceEntryType === "讲解服务"
          ? `${option.name}讲解服务`
          : option.name;
      spuTableName = "resource_point_spu";
      spuId = option.id;
      spuName = option.name;
      if (pointItem) {
        skuTableName = "resource_point_sku";
        skuId = pointItem.skuId;
        skuName = pointItem.skuName;
      } else if (freeSkuName) {
        skuTableName = "resource_point_sku";
        skuId = null;
        skuName = freeSkuName;
      }
    }
  } else if (resourceType === "大交通") {
    transport = structuredTransport ?? parseTransport(text);
    if (!transport.ok) return transport.result;
    itemName = transportItemName(transport);
    resourceEntryType = transport.entryType;
  }

  const formulaText = quantityFormulaText(text);
  const people = multiplicativeFormula?.quantityFactor
    ?? matchNumber(formulaText, /(\d+)\s*人/)
    ?? (resourceType === "大交通"
      ? matchNumber(formulaText, /(?:学生|老师|导游|司机)\s*(\d+)\s*[xX×*]\s*\d+(?:\.\d+)?\s*(?:元)?$/)
      : null);
  const nights = matchNumber(formulaText, /(\d+)\s*晚/);
  const meals = matchNumber(formulaText, /(\d+)\s*餐/);
  const days = matchNumber(formulaText, /(\d+)\s*天/);
  const vehicles = matchNumber(formulaText, /(\d+)\s*辆/);
  const projects = matchNumber(formulaText, /(\d+)\s*个?项目/);
  let unit: string;
  let quantity: number;
  let nightCount: number | null = null;
  let dayCount: number | null = null;

  const hasExplicitPeople = people !== null;
  if (multiplicativeFormula && resourceType === "考察点") {
    unit = "人次";
    quantity = multiplicativeFormula.quantityFactor * multiplicativeFormula.billingFactor;
  } else if (multiplicativeFormula && resourceType === "餐") {
    unit = "人餐";
    quantity = multiplicativeFormula.quantityFactor * multiplicativeFormula.billingFactor;
  } else if (resourceType === "杂费" && projects !== null) {
    unit = people !== null ? "人项" : "项";
    quantity = (people ?? 1) * projects;
  } else if (nights !== null) {
    unit = "人晚";
    nightCount = nights;
    quantity = (people ?? 1) * nights;
  } else if (meals !== null) {
    unit = "人餐";
    quantity = (people ?? 1) * meals;
  } else if ((resourceType === "导游" || (resourceType === "杂费" && Boolean(role))) && days !== null) {
    unit = "人天";
    dayCount = days;
    quantity = (people ?? 1) * days;
  } else if (resourceType === "包车" && vehicles !== null) {
    unit = "辆";
    quantity = vehicles;
  } else if (resourceType === "包车" && days !== null) {
    unit = "车天";
    dayCount = days;
    quantity = days;
  } else {
    unit = resourceType === "大交通" || resourceType === "考察点"
      ? "人次"
      : (resourceType === "杂费" || resourceType === "包车") && role
        ? "人"
        : "项";
    quantity = people ?? 1;
  }
  if (quantity <= 0) return failure("quantity_invalid", "quantity", "请按格式示例补充数量");

  return validateSinglePreviewResult({
    ok: true,
    preview: {
      resourceType,
      ...(resourceEntryType ? { resourceEntryType } : {}),
      subtype: resourceType === "考察点" && resourceEntryType
        ? resourceEntryType
        : detectSubtype(text, resourceType),
      itemName,
      spec: transport?.ok && transport.seatClass
        ? transport.seatClass
        : detectSpec(text, resourceType, role),
      unit,
      travelerRole: role,
      mealType: resourceType === "餐" ? detectMealType(text) : null,
      mealCount: unit === "人餐" ? multiplicativeFormula?.billingFactor ?? meals : null,
      quantity,
      unitPrice: price.value,
      unitPriceText: price.text,
      nightCount,
      dayCount,
      sourceText,
      isLinkedToPeopleCount: Boolean(role) && !hasExplicitPeople && new Set(["人", "人晚", "人餐", "人次", "人天"]).has(unit),
      manualPeopleCount: hasExplicitPeople ? people : null,
      ...(spuTableName ? { spuTableName, spuId, spuName } : {}),
      ...(skuTableName ? { skuTableName, skuId, skuName } : {}),
      ...(transport?.ok ? {
        transportMode: transport.mode,
        fromPoint: transport.from,
        toPoint: transport.to,
        routeCode: transport.routeCode,
        seatClass: transport.seatClass,
      } : {}),
    },
  });
}

type MealFactorState = {
  stayArea: string | null;
  travelerRole: string | null;
  mealType: string | null;
};

type MealFactorRecordResult = {
  result: ItineraryResourceEntriesParseResult;
  state: MealFactorState;
};

/** 删除餐费片段中已经结构化识别的角色或餐型 token。 */
function removeMealPrefixToken(text: string, token: string | null): string {
  if (!token) return text.trim();
  const index = text.indexOf(token);
  if (index < 0) return text.trim();
  return `${text.slice(0, index)} ${text.slice(index + token.length)}`.trim().replace(/\s+/g, " ");
}

/**
 * 解析餐费的紧凑批量语法；地点、角色和餐型仅在本次输入中向后继承。
 * 每个片段仍回到单条共享 parser，保证候选校验、字段映射和数值门禁一致。
 */
function parseMealFactorRecord(
  rawText: string,
  context: ItineraryResourceParseContext,
  inheritedState: MealFactorState,
): MealFactorRecordResult {
  const state = { ...inheritedState };
  const previews: ItineraryResourcePreview[] = [];
  const segments = rawText.split(/[,，]/u).map((segment) => segment.trim());
  if (segments.some((segment) => !segment)) {
    const result = failure("meal_segment_empty", "text", "餐费分隔符前后都需要填写完整计价项");
    if (result.ok) throw new Error("unreachable");
    return { result, state };
  }

  const stayAreas = [...context.entryOptions.stayAreas]
    .sort((left, right) => right.name.length - left.name.length);
  for (const sourceText of segments) {
    const normalized = normalizeItineraryResourceNumericText(sourceText);
    const syntaxFailure = numericSyntaxFailure(normalized, "餐");
    if (syntaxFailure) {
      if (syntaxFailure.ok) throw new Error("unreachable");
      return { result: syntaxFailure, state };
    }
    const formula = parseMultiplicativePriceFormula(normalized);
    if (!formula) {
      const result = failure(
        "meal_formula_invalid",
        "quantity",
        "餐费请按“人数 x 单价”或“人数 x 餐数 x 单价”输入",
      );
      if (result.ok) throw new Error("unreachable");
      return { result, state };
    }

    const normalizedPrefix = formula.prefix.replace(/^@/u, "").trim();
    const explicitStayArea = stayAreas.find((stayArea) => normalizedPrefix.startsWith(stayArea.name));
    let remainingPrefix = normalizedPrefix;
    if (explicitStayArea) {
      state.stayArea = explicitStayArea.name;
      remainingPrefix = normalizedPrefix.slice(explicitStayArea.name.length).trim();
    } else if (formula.prefix.trim().startsWith("@")) {
      const candidate = firstToken(formula.prefix);
      const result = failure(
        "entry_option_not_found",
        "stayArea",
        `未找到行程住宿区域“${candidate}”，请从 @ 候选中选择`,
        candidate,
        stayAreas.map((stayArea) => stayArea.name),
      );
      if (result.ok) throw new Error("unreachable");
      return { result, state };
    }

    const roleMentions = separatedRoleMatches(remainingPrefix);
    const compactRole = remainingPrefix.match(/^(学生|老师|导游|司机)(?=$|早餐|中餐|晚餐|正餐)/u)?.[1] ?? null;
    if (roleMentions.length > 1) {
      const result = travelerRoleAmbiguousFailure();
      if (result.ok) throw new Error("unreachable");
      return { result, state };
    }
    const explicitRole = roleMentions[0] ?? compactRole;
    const explicitMealType = detectMealType(remainingPrefix);
    if (explicitRole) state.travelerRole = explicitRole;
    if (explicitMealType) state.mealType = explicitMealType;
    remainingPrefix = removeMealPrefixToken(remainingPrefix, explicitRole);
    remainingPrefix = removeMealPrefixToken(remainingPrefix, explicitMealType);
    if (remainingPrefix) {
      const result = failure(
        "meal_prefix_invalid",
        "text",
        `餐费片段包含无法识别的内容“${remainingPrefix}”`,
        remainingPrefix,
      );
      if (result.ok) throw new Error("unreachable");
      return { result, state };
    }
    if (!state.stayArea) {
      const result = failure("meal_stay_area_required", "stayArea", "餐费首项需要填写 @ 地点");
      if (result.ok) throw new Error("unreachable");
      return { result, state };
    }
    if (!state.mealType) {
      const result = failure("meal_type_required", "mealType", "餐费首项需要填写早餐、中餐、晚餐或正餐");
      if (result.ok) throw new Error("unreachable");
      return { result, state };
    }

    const canonicalText = [
      `@${state.stayArea}`,
      state.travelerRole,
      state.mealType,
      `${formula.quantityFactorText}人 x ${formula.billingFactor}餐 x ${formula.unitPriceText}元`,
    ].filter(Boolean).join(" ");
    const parsed = parseItineraryResourceEntry(canonicalText, context);
    if (!parsed.ok) return { result: parsed, state };
    previews.push({ ...parsed.preview, sourceText });
  }
  return { result: { ok: true, previews }, state };
}

/** 按非空行批量解析，保持输入顺序并采用全有或全无规则。 */
export function parseItineraryResourceEntries(
  rawText: string,
  context?: ItineraryResourceParseContext,
): ItineraryResourceEntriesParseResult {
  const previews: ItineraryResourcePreview[] = [];
  let mealFactorState: MealFactorState = {
    stayArea: null,
    travelerRole: null,
    mealType: null,
  };
  let inheritedPointName: string | null = null;
  const rows = rawText.replace(/[;；]/g, "\n").split(/\r?\n/);
  for (const [index, row] of rows.entries()) {
    if (!row.trim()) continue;
    const lineNumber = index + 1;
    const lengthFailure = entryTextLengthFailure(row.trim());
    if (lengthFailure) {
      if (lengthFailure.ok) throw new Error("unreachable");
      const reason = { ...lengthFailure.reason, lineNumber };
      return { ok: false, error: `第 ${lineNumber} 行：${lengthFailure.error}`, reason };
    }
    const trimmedRow = row.trim();
    const pointCandidate = context?.activeResourceType === "考察点"
      ? [...context.entryOptions.points]
          .sort((left, right) => right.name.length - left.name.length)
          .find((candidate) => trimmedRow.replace(/^@/u, "").startsWith(candidate.name))
      : undefined;
    if (pointCandidate && trimmedRow.startsWith("@")) inheritedPointName = pointCandidate.name;
    const effectiveRow = context?.activeResourceType === "考察点"
      && trimmedRow.startsWith("#")
      && inheritedPointName
      ? `@${inheritedPointName} ${trimmedRow}`
      : row;
    const normalizedRow = normalizeItineraryResourceNumericText(effectiveRow);
    const rowResourceType = context?.activeResourceType || detectResourceType(normalizedRow);
    const syntaxFailure = numericSyntaxFailure(
      normalizedRow,
      rowResourceType,
    );
    if (syntaxFailure) {
      if (syntaxFailure.ok) throw new Error("unreachable");
      const reason = { ...syntaxFailure.reason, lineNumber };
      return { ok: false, error: `第 ${lineNumber} 行：${syntaxFailure.error}`, reason };
    }
    const pointRoleRecord = context?.activeResourceType === "考察点"
      && normalizedRow.includes("#")
      && /(学生|老师|导游|司机)\s*\d/u.test(normalizedRow)
      ? parsePointRoleRecord(effectiveRow, context)
      : null;
    const attachedRoleMentions = pointRoleRecord ? [] : attachedRoleValueMatches(normalizedRow, rowResourceType);
    const separatedRoleMentions = pointRoleRecord ? [] : separatedRoleMatches(normalizedRow);
    if (
      attachedRoleMentions.length > 0
      && attachedRoleMentions.length + separatedRoleMentions.length > 1
    ) {
      const ambiguousRole = travelerRoleAmbiguousFailure();
      if (ambiguousRole.ok) throw new Error("unreachable");
      const reason = { ...ambiguousRole.reason, lineNumber };
      return { ok: false, error: `第 ${lineNumber} 行：${ambiguousRole.error}`, reason };
    }
    const mealFactorRecord = context?.activeResourceType === "餐"
      && /\d+(?:\.\d+)?\s*[xX×*]/u.test(normalizedRow)
      ? parseMealFactorRecord(row, context, mealFactorState)
      : null;
    const batchResult = pointRoleRecord ?? mealFactorRecord?.result ?? (context?.activeResourceType === "包车"
      && isStructuredBusRecord(normalizedRow)
      ? parseBusRecord(row, context)
      : context?.activeResourceType === "包车" && /车队\s*\d+\s*(?:[xX×*]|辆)/.test(normalizedRow)
        ? parseSimpleBusRecord(row, context)
      : context?.activeResourceType === "大交通"
        && ((normalizedRow.match(/学生|老师|导游|司机/g)?.length ?? 0) > 1)
        ? parseTransportRecord(row, context)
      : context?.activeResourceType === "住宿"
        && isStayRoleRecord(normalizedRow)
        ? parseStayRecord(row, context)
      : context?.activeResourceType === "杂费"
        && /[,，]/.test(normalizedRow)
        && /(学生|老师|导游|司机)\s*(?:[xX×*]\s*)?\d/.test(normalizedRow)
        ? parseMiscRoleRecord(row, context)
        : null);
    if (batchResult) {
      if (!batchResult.ok) {
        const reason = { ...batchResult.reason, lineNumber };
        return { ok: false, error: `第 ${lineNumber} 行：${batchResult.error}`, reason };
      }
      for (const preview of batchResult.previews) {
        const unsafeResult = previewNumericSafetyFailure(preview);
        if (!unsafeResult) continue;
        if (unsafeResult.ok) throw new Error("unreachable");
        const reason = { ...unsafeResult.reason, lineNumber };
        return { ok: false, error: `第 ${lineNumber} 行：${unsafeResult.error}`, reason };
      }
      if (mealFactorRecord) mealFactorState = mealFactorRecord.state;
      previews.push(...batchResult.previews.map((preview) => ({
        ...preview,
        sourceText: trimmedRow,
        lineNumber,
      })));
      continue;
    }
    const singleResult = parseItineraryResourceEntry(effectiveRow, context);
    if (!singleResult.ok) {
      const reason = { ...singleResult.reason, lineNumber };
      return { ok: false, error: `第 ${lineNumber} 行：${singleResult.error}`, reason };
    }
    previews.push({ ...singleResult.preview, sourceText: trimmedRow, lineNumber });
  }
  if (previews.length) return { ok: true, previews };
  const result = failure("input_empty", "text", "请输入资源信息");
  if (result.ok) throw new Error("unreachable");
  return result;
}
